/**
 * Split bars (beats) on the web: the copy and the bits of bookkeeping the
 * editor needs around the shared bar operations in `@chordbaker/music-theory`
 * (`splitSlotAt`, `setSlotBeats`, `removeSlotFromBar`, `clearBeats`...).
 * English only, like the rest of the web app; React Native localizes the same
 * strings under `chart.beats.*` / `chart.barEditor.*`. Rules: docs/plans/split-bars-beats.md.
 */
import {
  clearBeats,
  getChordDisplayText,
  getRowMeters,
  rowHasBeats,
  type BarSlotLayout,
  type BeatDot,
  type SplitRestSide,
} from '@chordbaker/music-theory';
import type { BarState, RowState, SectionState, SlotState, TimeSignature } from '../types';
import { generateUniqueId } from './idUtils';

/** A length or beat number with ½ for halves: 0.5 → "½", 1.5 → "1½", 2 → "2". */
export const formatBeats = (value: number): string => {
  const whole = Math.floor(value);
  const half = value - whole >= 0.5;
  if (!half) return String(whole);
  return whole === 0 ? '½' : `${whole}½`;
};

/** Spoken length of a slot: "Half a beat", "1 beat", "2 and a half beats". */
export const describeBeats = (value: number): string => {
  const whole = Math.floor(value);
  const half = value - whole >= 0.5;
  if (whole === 0) return 'Half a beat';
  if (half) return `${whole} and a half beats`;
  return whole === 1 ? '1 beat' : `${whole} beats`;
};

/** The dot layout is shared with Buffet's preview. */
export { buildBeatDots, type BeatDot } from '@chordbaker/music-theory';

/** Size of a whole beat dot; a half dot is half as wide. */
export const BEAT_DOT_SIZE = 6;

/** Width the dots take with `gap` between dots (twice that where a group starts). */
export const beatDotsWidth = (dots: BeatDot[], gap: number): number =>
  dots.reduce((width, dot, i) => width + (dot.half ? BEAT_DOT_SIZE / 2 : BEAT_DOT_SIZE) + (i > 0 ? gap : 0) + (dot.groupStart ? gap : 0), 0);

/** How a slot is named in messages: its chord (or custom text), or "the rest" for a slot with neither. */
export const slotName = (slot: SlotState | undefined): string =>
  slot && (slot.chord || slot.customText) ? getChordDisplayText(slot) : 'the rest';

/** "C", "C and G", "C, G and Am" */
const listNames = (names: string[]): string =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export const capitalize = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

export const CANT_ADD_INSIDE_TITLE = "Can't add inside a split bar";

/** Why + before/after is dimmed on an inner slot of a split bar. */
export const cantAddInsideMessage = (bar: BarState): string => {
  const names = bar.slots.map(slotName);
  return capitalize(
    `${listNames(names)} share this bar. Add a bar before ${names[0]} or after ${names[names.length - 1]}, ` +
      'or shorten a slot to make room for another chord.',
  );
};

/**
 * The rows whose split bars a time-signature change would break: rows with
 * beats whose meter differs between `before` and `after` (meters carry
 * forward, so a row's change can reach the rows after it).
 */
export const rowsLosingBeats = (
  before: { sections: SectionState[]; timeSignature: TimeSignature | null },
  after: { sections: SectionState[]; timeSignature: TimeSignature | null },
): Set<string> => {
  const oldMeters = getRowMeters(before.sections, before.timeSignature);
  const newMeters = getRowMeters(after.sections, after.timeSignature);
  const affected = new Set<string>();
  for (const section of before.sections) {
    for (const row of section.rows) {
      const was = oldMeters.get(row.id);
      const will = newMeters.get(row.id);
      const changes = !was || !will || was.numerator !== will.numerator || was.denominator !== will.denominator;
      if (changes && rowHasBeats(row)) {
        affected.add(row.id);
      }
    }
  }
  return affected;
};

/** The sections with beats cleared in the given rows: each of their slots becomes a bar of its own. */
export const clearBeatsInRows = (sections: SectionState[], rowIds: Set<string>): SectionState[] =>
  rowIds.size === 0
    ? sections
    : sections.map(section => ({
      ...section,
      rows: section.rows.map(row => (rowIds.has(row.id) ? clearBeats(row, generateUniqueId) : row)),
    }));

/** Title and message of the dialog shown before a time-signature change clears beats. */
export const meterChangeBeatsDialog = (
  timeSignature: TimeSignature | null,
  scope: 'song' | 'row' | 'following',
  alsoClearsRowSignatures = false,
): { title: string; message: string } => {
  const subject = {
    song: 'This song has',
    row: 'This row has',
    following: 'Rows that use this time signature have',
  }[scope];
  return {
    title: timeSignature ? `Change to ${timeSignature.numerator}/${timeSignature.denominator}?` : 'Remove the time signature?',
    message:
      `${subject} bars split into beats. Changing the time signature turns them back into regular slots, one bar each.` +
      (alsoClearsRowSignatures ? ' It also replaces the time signatures set on individual rows.' : ''),
  };
};

/**
 * Where a `+` cuts a segment: in the middle, on a whole beat when one is in
 * reach, and when the middle is off the grid the chord keeps the longer part
 * (3 beats → 2 + 1 either way). Returns the cut for a rest on each side, or
 * null when the segment is too short to split. Same rule as RN's
 * `BarStrip.splitPoints`.
 */
export function splitPoints(segment: BarSlotLayout, minUnits: number): { side: SplitRestSide; at: number }[] | null {
  if (segment.lengthUnits < 2 * minUnits - 1e-9) return null;
  const start = segment.startUnits;
  const end = start + segment.lengthUnits;
  const middle = start + segment.lengthUnits / 2;
  const snapUp = Math.ceil(middle / minUnits - 1e-9) * minUnits;
  const snapDown = Math.floor(middle / minUnits + 1e-9) * minUnits;
  const wholeUp = Math.ceil(middle - 1e-9);
  const wholeDown = Math.floor(middle + 1e-9);
  return [
    { side: 'before', at: wholeDown >= start + minUnits - 1e-9 ? wholeDown : snapDown },
    { side: 'after', at: wholeUp <= end - minUnits + 1e-9 ? wholeUp : snapUp },
  ];
}
